import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts'
import type {
  AuthenticationPort,
  UserProfile,
  UserSession,
} from '@/domains/auth/AuthenticationPort'
import type { RuntimeConfig } from '@/infra/env/getEnv'

const getOrigin = (): string => globalThis.location.origin

/**
 * Maps the OIDC User object to our internal UserSession format.
 * Requires profile.sub — use mapUserToProfile for display-only data.
 */
const mapUserToSession = (user: User): UserSession => {
  const profile = user.profile as Record<string, string>
  const userId = profile.sub
  if (!userId) throw new Error('Missing user ID in OIDC profile')

  return {
    id: userId,
    name: profile.givenName || profile.name,
    email: profile.mail || profile.email,
    accessToken: user.access_token,
  }
}

/**
 * Extracts display profile data from an OIDC User (does not require sub).
 */
const mapUserToProfile = (user: User): UserProfile => {
  const profile = user.profile as Record<string, string>
  return {
    firstName: profile.givenName || profile.name || '',
    mail: profile.mail || profile.email || '',
  }
}

export const createOidcAuthenticationAdapter = (cfg: RuntimeConfig): AuthenticationPort => {
  /**
   * We use localStorage instead of sessionStorage for production stability.
   * SessionStorage is lost if the tab is refreshed or on some mobile redirects.
   */
  const persistentStore = new WebStorageStateStore({ store: globalThis.localStorage })

  const userManager = new UserManager({
    authority: cfg.OIDC_AUTHORITY,
    client_id: cfg.OIDC_CLIENT_ID,
    redirect_uri: `${getOrigin()}/token`,
    silent_redirect_uri: `${getOrigin()}/silent-renew`,
    response_type: 'code', // PKCE is enabled by default with 'code' in oidc-client-ts
    scope: 'profile openid',
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 60,
    loadUserInfo: true,
    userStore: persistentStore,
    stateStore: persistentStore, // Critical: ensure state is also in localStorage
  })

  return {
    getUserProfile: async (): Promise<UserProfile> => {
      const user = await userManager.getUser()
      if (!user || user.expired) {
        console.warn('No active user session found')
        return { firstName: '', mail: '' }
      }
      return mapUserToProfile(user)
    },

    getCurrentUser: async (): Promise<UserSession | null> => {
      const user = await userManager.getUser()
      return user && !user.expired ? mapUserToSession(user) : null
    },

    isAuthenticated: async (): Promise<boolean> => {
      const user = await userManager.getUser()
      return Boolean(user && !user.expired)
    },

    login: async (returnTo?: string): Promise<void> => {
      const currentLocation = globalThis.location.pathname + globalThis.location.search

      await userManager.signinRedirect({
        state: { returnTo: returnTo || currentLocation },
        /**
         * Local-only logout strategy: logout() only clears the local session without
         * invalidating the SSO session on other applications. prompt: 'login' forces
         * re-authentication on this app while leaving other SSO sessions intact.
         */
        extraQueryParams: { prompt: 'login' },
      })
    },

    logout: async (): Promise<void> => {
      await userManager.removeUser()
      await userManager.clearStaleState()
      globalThis.location.href = getOrigin()
    },

    trySilentRefresh: async (): Promise<string | null> => {
      try {
        const refreshedUser = await userManager.signinSilent()
        return refreshedUser?.access_token ?? null
      } catch {
        return null
      }
    },

    /**
     * Optimized callback handler to prevent "No matching state" errors.
     * This handles cases where React StrictMode or double-clicks trigger the callback twice.
     */
    handleSigninCallback: async (): Promise<{ returnTo?: string }> => {
      try {
        const user = await userManager.signinCallback()
        const state = user?.state as { returnTo?: string } | undefined
        return { returnTo: state?.returnTo }
      } catch (err) {
        /**
         * If we get a "No matching state" error, the state might have already been
         * consumed by a previous render. We verify if a valid session exists.
         */
        const currentUser = await userManager.getUser()
        if (currentUser && !currentUser.expired) {
          console.info('Callback already processed, session recovered from store.')
          const state = currentUser.state as { returnTo?: string } | undefined
          return { returnTo: state?.returnTo }
        }

        throw err instanceof Error ? err : new Error('Signin callback failed')
      }
    },

    handleSilentRenew: async () => {
      await userManager.signinSilentCallback()
    },
  }
}
